import { useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useSignContentUpload, useRecordContentAsset } from './backend/marketing'
import { writeErrorMessage } from '../utils/writeError'

// 200 MB, matching the bucket limit set in db/migrations/0031. The documents
// dialog caps at 15 MB because it sends the file as base64 through the Worker and
// Postgres; this path does not, so the limit is the bucket's rather than the
// transport's.
export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024

export type UploadTarget = {
  contentItemId?: string | null
  designRequestId?: string | null
  /** Reference is what the requester sends in. Artwork is the work itself. */
  kind?: 'Reference' | 'Artwork'
}

export type UploadOutcome = {
  /** Files whose bytes landed AND whose row was written. Nothing else counts. */
  done: number
  /** One line per file that did not, each already written for a person to read. */
  failed: string[]
  /** Chosen but never attempted, because they are over the bucket limit. */
  tooBig: string[]
}

/**
 * The three step upload, in one place, because two callers need it: the button
 * on an existing record, and the request dialog, which cannot upload until the
 * request it is attaching to exists.
 *
 * THREE STEPS, AND WHY IT IS NOT ONE
 *
 *   1. Ask the Worker for a one-shot signed URL. The Worker composes the storage
 *      path and checks the permission, so neither the path nor the permission is
 *      ever the browser's decision.
 *   2. PUT the bytes straight to Supabase Storage with that URL.
 *   3. Tell the Worker the bytes landed, which is what writes the catalogue row.
 *
 * The obvious single-call alternative is what the documents dialog does: read
 * the file to base64 and post it to the Worker. It works, and it is why that
 * dialog caps at 15 MB, because those bytes travel through a Postgres connection
 * on a project whose entire pool is sixty connections wide. Marketing content is
 * reels. A 60 MB clip becomes 80 MB of base64 and would sit in that pool for the
 * duration of the upload.
 *
 * The cost of three steps is honest: an upload that dies between 2 and 3 leaves
 * an object in the bucket that no row points at. That is invisible and harmless.
 * The other ordering, writing the row first, would leave a file in the list that
 * nobody can open, which an operator has to notice and report before anyone knows
 * it happened. A missing figure shows as missing, never as something else.
 *
 * THIS IS NOT A SECOND READ PATH INTO POSTGRES. lib/supabase.ts holds the line
 * that the browser gets a session token and nothing else, because no view sets
 * security_invoker and pointing an authenticated client at those tables would
 * leak one driver's earnings to another. Storage is not Postgres, and a signed
 * upload URL authorises exactly one object at exactly one key that the server
 * chose. No table is reachable through it.
 */
export function useMarketingUpload() {
  const sign = useSignContentUpload()
  const record = useRecordContentAsset()
  const signTrigger = sign.trigger
  const recordTrigger = record.trigger

  const upload = useCallback(
    async (files: File[], target: UploadTarget): Promise<UploadOutcome> => {
      const kind = target.kind ?? 'Artwork'
      const contentItemId = target.contentItemId ?? null
      const designRequestId = target.designRequestId ?? null

      const tooBig = files.filter((f) => f.size > MAX_UPLOAD_BYTES).map((f) => f.name)
      const sendable = files.filter((f) => f.size <= MAX_UPLOAD_BYTES)

      // Counted rather than assumed. Several files can be chosen at once and one
      // of them can fail on its own; saying "3 of 4 uploaded" is the only honest
      // report of that, and a single "uploaded" toast would hide it.
      let done = 0
      const failed: string[] = []

      for (const file of sendable) {
        try {
          const signed = (await signTrigger({
            content_item_id: contentItemId,
            design_request_id: designRequestId,
            filename: file.name,
            kind,
          }).result) as { path: string; token: string; bucket: string }

          const { error } = await supabase.storage
            .from(signed.bucket)
            .uploadToSignedUrl(signed.path, signed.token, file, {
              contentType: file.type || 'application/octet-stream',
            })
          if (error) throw new Error(error.message)

          await recordTrigger({
            content_item_id: contentItemId,
            design_request_id: designRequestId,
            storage_path: signed.path,
            filename: file.name,
            mime_type: file.type || null,
            size_bytes: file.size,
            kind,
          }).result
          done += 1
        } catch (err) {
          failed.push(`${file.name}: ${writeErrorMessage(err)}`)
        }
      }

      return { done, failed, tooBig }
    },
    [signTrigger, recordTrigger],
  )

  return { upload }
}
